import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

const PRIMARY = "var(--portal-theme-primary)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const isEmpty = (value) => {
  if (value == null) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

const getQuestionId = (q) => String(q?.id || q?.questionId || "");

const buildInitialAnswers = (questions = [], existing = []) => {
  const map = {};
  (existing || []).forEach((entry) => {
    const id = String(entry?.questionId || "");
    if (id) map[id] = entry.value;
  });
  (questions || []).forEach((q) => {
    const id = getQuestionId(q);
    if (!id) return;
    if (!(id in map)) map[id] = "";
  });
  return map;
};

/**
 * Coach review form driven by tracker.coachReview.questions (admin settings).
 */
export default function CoachReviewPanel({
  tracker,
  submission,
  disabled = false,
  saving = false,
  onSubmit,
}) {
  const coachReview = tracker?.coachReview || {};
  const questions = Array.isArray(coachReview.questions)
    ? coachReview.questions
    : [];
  const enabled = coachReview.enabled !== false;

  const [answers, setAnswers] = useState(() =>
    buildInitialAnswers(questions, submission?.coachReviewAnswers)
  );

  useEffect(() => {
    setAnswers(
      buildInitialAnswers(questions, submission?.coachReviewAnswers)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission?.id, submission?.coachReviewAnswers, tracker?.id]);

  const needsAction = useMemo(() => {
    const status = submission?.reviewStatus;
    return status === "pending" || status === "overdue";
  }, [submission?.reviewStatus]);

  if (!enabled || !questions.length) {
    return null;
  }

  const setAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const handleComplete = async () => {
    if (!onSubmit) return;
    const requireAll = Boolean(coachReview.requireAllQuestions);
    for (const q of questions) {
      const must = requireAll || q?.required === true || q?.isRequired === true;
      if (!must) continue;
      const id = getQuestionId(q);
      if (isEmpty(answers[id])) {
        return onSubmit({
          error: `Please answer: ${q.label || q.statement || "required question"}`,
        });
      }
    }
    await onSubmit({
      coachReviewAnswers: answers,
      reviewStatus: "reviewed",
    });
  };

  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        bgcolor: "#fff",
        mb: 2.5,
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
            Coach review
          </Typography>
          <Typography sx={{ mt: 0.35, fontSize: 13, color: MUTED }}>
            {submission?.reviewDetail
              ? submission.reviewDetail
              : needsAction
                ? "Complete the questions below, then mark as reviewed."
                : "Questions from this tracker’s review settings."}
          </Typography>
        </Box>
        {needsAction ? (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: submission?.reviewStatus === "overdue" ? "#dc2626" : "#b45309",
              flexShrink: 0,
            }}
          >
            {submission?.reviewStatus === "overdue" ? "Overdue" : "Due"}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
        {questions.map((q) => {
          const id = getQuestionId(q);
          const fieldType = String(q.fieldType || q.type || "short_text");
          const label = q.label || q.statement || "Question";
          const required =
            Boolean(coachReview.requireAllQuestions) ||
            q.required === true ||
            q.isRequired === true;
          const options = Array.isArray(q.options) ? q.options : [];
          const scaleMin = Number(q.scaleMin) || 1;
          const scaleMax = Number(q.scaleMax) || 10;

          if (fieldType === "long_text") {
            return (
              <TextField
                key={id}
                fullWidth
                multiline
                minRows={3}
                label={label}
                required={required}
                value={answers[id] ?? ""}
                disabled={disabled || saving}
                onChange={(e) => setAnswer(id, e.target.value)}
                helperText={q.helpText || ""}
                sx={{ mb: 2 }}
              />
            );
          }

          if (
            fieldType === "single_choice" ||
            fieldType === "select" ||
            fieldType === "dropdown"
          ) {
            return (
              <TextField
                key={id}
                select
                fullWidth
                label={label}
                required={required}
                value={answers[id] ?? ""}
                disabled={disabled || saving}
                onChange={(e) => setAnswer(id, e.target.value)}
                helperText={q.helpText || ""}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {options.map((opt) => {
                  const value =
                    typeof opt === "string" ? opt : opt?.value || opt?.label;
                  const optLabel =
                    typeof opt === "string" ? opt : opt?.label || opt?.value;
                  return (
                    <MenuItem key={String(value)} value={value}>
                      {optLabel}
                    </MenuItem>
                  );
                })}
              </TextField>
            );
          }

          if (fieldType === "scale" || fieldType === "number") {
            return (
              <TextField
                key={id}
                fullWidth
                type="number"
                label={label}
                required={required}
                value={answers[id] ?? ""}
                disabled={disabled || saving}
                onChange={(e) => setAnswer(id, e.target.value)}
                helperText={
                  q.helpText ||
                  (fieldType === "scale"
                    ? `Scale ${scaleMin}–${scaleMax}`
                    : "")
                }
                inputProps={
                  fieldType === "scale"
                    ? { min: scaleMin, max: scaleMax }
                    : undefined
                }
                sx={{ mb: 2 }}
              />
            );
          }

          return (
            <TextField
              key={id}
              fullWidth
              label={label}
              required={required}
              value={answers[id] ?? ""}
              disabled={disabled || saving}
              onChange={(e) => setAnswer(id, e.target.value)}
              helperText={q.helpText || ""}
              sx={{ mb: 2 }}
            />
          );
        })}

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
          <button
            type="button"
            className="small-contained-button"
            disabled={disabled || saving}
            onClick={handleComplete}
          >
            {saving ? "Saving…" : "Complete review"}
          </button>
          {submission?.reviewStatus === "reviewed" ? (
            <Typography
              sx={{
                alignSelf: "center",
                fontSize: 13,
                color: PRIMARY,
                fontWeight: 600,
              }}
            >
              Marked reviewed
              {submission.reviewedAt
                ? ` · ${new Date(submission.reviewedAt).toLocaleString()}`
                : ""}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
